<script lang="ts">
    import {AliiConnectionType, type IdName} from '$lib/models/types';
    import {AliiOCRTypes, ClientStatuses} from '$lib/models/client/types';
    import {onMount} from 'svelte';
    import {aliiExportTypeStore} from '$lib/stores/aliiExportTypeStore';
    import {aliiConnectTypeStore} from '$lib/stores/aliiConnectTypeStore';
    import {clientStore} from '$lib/stores/clientStore';
    import {mailOcrEnabled} from '$lib/stores/settingsStore';
    import {Copy, XCircle} from 'phosphor-svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientName: string | undefined;
  export let clientStatus: number;
  export let connectionTypeId: number;
  export let exportTypeId: number;
  export let aliiOCRType: number;
  export let nanonetModelKey: string | undefined;
  export let clientKey: string | undefined;
  export let clientId: string | undefined;
  export let location: string | undefined;
  export let language: number | undefined;
  export let domains: IdName[];
  export let organisationId: string | undefined = undefined;
  export let organisationName: string | undefined = undefined;

  let newDomain: string = '';

  let exportTypes: IdName[] = [];
  let connectionTypes: IdName[] = [];
  let clientLocations: IdName[] = [];
  let clientLanguages: IdName[] = [];

  const hiddenConnectionTypes: AliiConnectionType[] = [AliiConnectionType.XeroV2];

  $: filteredConnectionTypes = connectionTypes.filter(ct => !hiddenConnectionTypes.includes(ct.name as AliiConnectionType));

  onMount(async () => {
    exportTypes = await aliiExportTypeStore.get();
    connectionTypes = await aliiConnectTypeStore.get();
    clientLocations = await clientStore.getLocations();
    clientLanguages = await clientStore.getLanguages();
    await checkExportType();
  });

  function onRemoveDomain(index: number): void {
    domains = domains.filter((_, i) => i !== index);
  }

  function onDomainAdd(): void {
    domains = [...domains, { id: undefined, name: newDomain }];
    newDomain = '';
  }
  async function copyToClipboard(value: string | undefined, label: string) {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      showToast({
        title: 'Success',
        message: `${label} copied to clipboard`,
        type: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Error',
        message: `Failed to copy ${label} to clipboard`,
        type: 'error',
      });
    }
  }

  const checkExportType = async () => {
    var exportTypeName = exportTypes.find((type) => type.id === exportTypeId)?.name ?? ''
    if (!exportTypeName || exportTypeName.toLowerCase() === 'none') {
      return;
    }
    var isConfigured = await checkExportTypeConfigured(exportTypeName);
    if (!isConfigured) {
      showToast({
        title: 'Missing configuration',
        message: `${exportTypeName} export type is not configured for this client. Ensure you configure it before proceeding.`,
        type: 'info',
      });
    }
  };
  async function checkExportTypeConfigured(exportTypeName) {
    try {
      const response = await fetchWithAuth(`/api/export/is-configured/${clientId}/${exportTypeName}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.configured) {
          return true;
        } else {
          return false;
        }
      }
    } catch (error) {
      console.error('Failed to check existing export config:', error);
      return false;
    }

  }
</script>

<div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
  <div>
    <div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Name</div>
        <div class="sm:col-span-4">
          <input class="input w-full" type="text" bind:value={clientName} />
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Client Status</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={clientStatus}>
            {#each ClientStatuses as status}
              <option value={status.id} selected={status.id === clientStatus}>{status.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Connection Type</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={connectionTypeId}>
            {#each connectionTypes as connectionType}
              <option value={connectionType.id} selected={connectionType.id === connectionTypeId}>{connectionType.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Export Type</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={exportTypeId} on:change={checkExportType}>
            {#each exportTypes as exportType}
              <option value={exportType.id} selected={exportType.id === exportTypeId}>{exportType.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">OCR Type</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={aliiOCRType} disabled={!$mailOcrEnabled}>
            {#each AliiOCRTypes as ocrType}
              <option value={ocrType.id} selected={ocrType.id === aliiOCRType}>{ocrType.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">
            Nanonet Model Key
            <div class="text-gray-500 text-xs italic">(leave blank for default)</div>
        </div>
        <div class="sm:col-span-4">
            <input class="input w-full" type="password" bind:value={nanonetModelKey} />
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Location</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={location}>
            {#each clientLocations as clientLocation}
            <option value={clientLocation.name} selected={clientLocation.name === location}>{clientLocation.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Language</div>
        <div class="sm:col-span-4">
          <select class="select w-full" bind:value={language}>
            {#each clientLanguages as clientLanguage}
            <option value={clientLanguage.id} selected={clientLanguage.id === language}>{clientLanguage.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Organisation</div>
        <div class="sm:col-span-4">
          <input class="input w-full" type="text" value={organisationName ?? '—'} readonly />
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Organisation ID</div>
        <div class="sm:col-span-4 flex items-center gap-2">
          <input class="input w-full" type="text" value={organisationId ?? '—'} readonly />
          <button
            type="button"
            class="rounded-md border border-transparent p-1.5 text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            on:click={() => copyToClipboard(organisationId, 'Organisation ID')}
            title="Copy Organisation ID to clipboard"
          >
            <Copy size={20} />
          </button>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Client ID</div>
        <div class="sm:col-span-4 flex items-center gap-2">
          <input class="input w-full" type="text" bind:value={clientId} readonly />
          <button
            type="button"
            class="rounded-md border border-transparent p-1.5 text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            on:click={() => copyToClipboard(clientId, 'Client ID')}
            title="Copy Client ID to clipboard"
          >
            <Copy size={20} />
          </button>
        </div>
      </div>
      <div class="mb-2 flex flex-col gap-4 sm:grid sm:grid-cols-6">
        <div class="font-bold sm:col-span-2">Client Key</div>
        <div class="sm:col-span-4 flex items-center gap-2">
          <input class="input w-full" type="text" bind:value={clientKey} readonly />
          <button
            type="button"
            class="rounded-md border border-transparent p-1.5 text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            on:click={() => copyToClipboard(clientKey, 'Client Key')}
            title="Copy Client Key to clipboard"
          >
            <Copy size={20} />
          </button>
        </div>
      </div>
    </div>
  </div>
  <div>
    <div>
      <div>
        <div class="mb-2 font-bold">Domains</div>
        <div class="mb-5 h-96 overflow-y-scroll rounded-md border bg-surface-500 p-3">
          {#each domains as domain, index}
            <div class="mb-2 inline-flex w-full items-center justify-between rounded-md border bg-white p-5">
              <div class="overflow-hidden overflow-ellipsis text-nowrap">
                <div>{domain.name}</div>
              </div>
              <div class="inline-flex gap-2">
                <button class="hover:text-error-500" on:click={() => onRemoveDomain(index)}>
                  <XCircle></XCircle>
                </button>
              </div>
            </div>
          {/each}
        </div>
        <div class="flex w-full place-content-center gap-5">
          <input class="input" bind:value={newDomain} />
          <button class="variant-filled-success btn place-self-center" on:click={onDomainAdd} disabled={!newDomain.trim()}> Add New Domain </button>
        </div>
      </div>
    </div>
  </div>
</div>
